import { useEffect, useId, useMemo, useState, type FC } from 'react';

import { Placeholder, useContent, useImageMapper, useRendering } from '@eneco/cms';
import { Box, Card, Grid, Heading, Image, PageGrid, Stack, Stretch, Tag, Text, TextLink } from '@eneco/sparky';
import { useI18nTranslation } from '@eneco/sparky/providers';

import type { ContentHubFilterArticle, ContentHubFilterContent, ContentHubFilterTag } from './content-hub-filter-content.types';
import { CONTENT_HUB_FILTER_SLOTS, resolveSlot } from './slots';

type FilterTag = {
  id: string;
  displayText: string;
  selected: boolean;
  onToggle: () => void;
};

// A tag is an item (`id`, `displayName`) on Sitecore and a `referenceDataTag` document (`_id`, `title`) on Sanity.
const tagId = (tag: ContentHubFilterTag): string => tag.id ?? tag._id ?? '';
const tagText = (tag: ContentHubFilterTag): string => tag.displayName ?? tag.title ?? '';

/** Mirrors the legacy `useContentFilter.ts`, including its state update inside `useMemo`. */
const useContentFilter = (articles: ContentHubFilterArticle[], tagList: ContentHubFilterTag[]) => {
  const [filteredArticles, setFilteredArticles] = useState(articles);

  const [tags, setTags] = useState<FilterTag[]>([]);

  useEffect(() => {
    setTags(prevTags => {
      return tagList.map(tag => {
        return {
          id: tagId(tag),
          displayText: tagText(tag),
          selected: prevTags.find(prevTag => prevTag.id === tagId(tag))?.selected ?? false,
          onToggle: () => {
            updateTags(tagId(tag));
          },
        };
      });
    });
  }, [tagList]);

  const updateTags = (selectedtagId: string) => {
    setTags(prevtags => {
      const updatedtags = prevtags.map(prevtag => {
        if (prevtag.id === selectedtagId) {
          return {
            ...prevtag,
            selected: !prevtag.selected,
          };
        }
        return prevtag;
      });
      return updatedtags;
    });
  };

  const selectedTags = useMemo(() => tags.filter(tag => tag.selected).map(tag => tag.id), [tags]);

  useMemo(() => {
    const filteredArticles = selectedTags.length
      ? articles.filter(article => {
          return (article.tags ?? []).some(tag => selectedTags.includes(tagId(tag)));
        })
      : articles;
    setFilteredArticles(filteredArticles);
  }, [articles, selectedTags]);

  const articleCount = filteredArticles.length;

  return { articleCount, filteredArticles, tags };
};

const TagNavigation: FC<{ tags: FilterTag[]; tagAriaLabel?: string; articleContainerId: string }> = ({
  tags,
  tagAriaLabel,
  articleContainerId,
}) => {
  const [resetButtonSelected, setResetButtonSelected] = useState(true);

  useEffect(() => {
    const isAnyTagSelected = tags.some(tag => tag.selected && tag.id !== 'reset');
    setResetButtonSelected(!isAnyTagSelected);
  }, [tags]);

  const resetButton = {
    id: 'reset',
    displayText: useI18nTranslation('resetFilters'),
    selected: resetButtonSelected,
    onClick: () => {
      tags.forEach(tag => {
        if (tag.selected) tag.onToggle();
      });

      setResetButtonSelected(true);
    },
  };

  return (
    <nav aria-label={tagAriaLabel}>
      <Stack alignX="start" direction="row" as="ul" gap={{ initial: 2, md: 4 }} wrap>
        {tags.map(tag => (
          <li key={tag.id}>
            <Tag onClick={tag.onToggle} isCurrent={tag.selected} ariaControls={articleContainerId}>
              {tag.displayText}
            </Tag>
          </li>
        ))}
        {!resetButton.selected && (
          <li>
            <TextLink key={resetButton.id} onClick={resetButton.onClick}>
              {resetButton.displayText}
            </TextLink>
          </li>
        )}
      </Stack>
    </nav>
  );
};

const Article: FC<{ article: ContentHubFilterArticle }> = ({ article }) => {
  const mapImage = useImageMapper();
  const { title, content, pageLinkText } = article;
  const image = mapImage(article.image);
  // Sitecore hands the article's address as the item's `url`; on Sanity it is the `link` the query adds.
  const href = article.link?.href ?? article.url;
  return (
    <Card overflow="hidden">
      <Stretch>
        <Stack>
          {image?.src ? (
            <Image width="100%" aspectRatio="2 / 1" objectFit="cover" src={image.src} sources={image.sources} alt={image.alt ?? ''} />
          ) : null}
          <Stack.Item grow>
            <Stretch>
              <Box padding="6">
                <Stretch>
                  <Stack gap="6" alignY="justify">
                    <Stack gap="6" alignY="justify">
                      <Heading as="h3" size="S">
                        {title}
                      </Heading>
                      {content ? <Text>{content}</Text> : null}
                    </Stack>
                    <TextLink emphasis="high" href={href}>
                      {pageLinkText}
                    </TextLink>
                  </Stack>
                </Stretch>
              </Box>
            </Stretch>
          </Stack.Item>
        </Stack>
      </Stretch>
    </Card>
  );
};

const ArticleList: FC<{ articles: ContentHubFilterArticle[]; id: string }> = ({ articles, id }) => {
  return (
    <Stretch>
      <Box id={id}>
        <Grid as="ul" columns={{ initial: 1, md: 2, lg: 3 }} gap={{ initial: 6, md: 10, lg: 16 }}>
          {articles.map(article => (
            <li key={article.id ?? article._key}>
              <Article article={article} />
            </li>
          ))}
        </Grid>
      </Box>
    </Stretch>
  );
};

const NO_TAGS: ContentHubFilterTag[] = [];
const NO_ARTICLES: ContentHubFilterArticle[] = [];

/**
 * `ContentHubFilter` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/ContentHubFilter/` (`ContentHubFilter.tsx`,
 * `useContentFilter.ts` and `components/`): the title, a tag filter, the article count, the introduction
 * slot and the articles that carry one of the selected tags. On Sanity the articles come from the page
 * query (`libs/cms-sanity/src/queries.ts#buildComputedBlockFields`), as the block stores only the rules
 * Sitecore lists them by.
 *
 * Drops the Experience Editor `wrap()` calls, which outside the editor return the plain field values, and
 * the legacy `useLinkComponent()` in favour of a plain `TextLink`, as in `Bucket.tsx`. Like the legacy
 * card, it prints an article's rich-text `content` as text, tags and all.
 */
const ContentHubFilter: FC = () => {
  const content = useContent<ContentHubFilterContent>();
  const introductionSlot = resolveSlot(useRendering(), CONTENT_HUB_FILTER_SLOTS.introduction);
  const { title, tagAriaLabel, singleArticleFoundLabel, noArticlesFoundLabel, multipleArticlesFoundLabel } = content;
  const articleContainerId = useId();
  const { tags, filteredArticles, articleCount } = useContentFilter(
    content.articles ?? NO_ARTICLES,
    content.tags ?? NO_TAGS,
  );

  const formatArticleCount = () => {
    switch (articleCount) {
      case 0:
        return noArticlesFoundLabel;
      case 1:
        return singleArticleFoundLabel;
      default:
        return multipleArticlesFoundLabel?.replace('{articleCount}', articleCount.toString());
    }
  };

  return (
    <Box paddingY="10">
      <PageGrid>
        <PageGrid.Item gridColumn="1/-1">
          <Stack gap="10" alignX={{ initial: 'start', md: 'center' }}>
            {title && <Heading as="h1">{title}</Heading>}
            <TagNavigation tags={tags} tagAriaLabel={tagAriaLabel} articleContainerId={articleContainerId} />
            <Text size="BodyXL">{formatArticleCount()}</Text>
            <Stretch width={true} height={'auto'}>
              <Grid columns={{ initial: 9, md: 12 }}>
                <Grid.Item gridColumn={{ initial: '1 / -1', md: '1 / -4' }}>
                  {introductionSlot ? <Placeholder name={introductionSlot} /> : null}
                </Grid.Item>
              </Grid>
            </Stretch>
            <ArticleList articles={filteredArticles} id={articleContainerId} />
          </Stack>
        </PageGrid.Item>
      </PageGrid>
    </Box>
  );
};

export default ContentHubFilter;
